import React from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { useGetJournalEventsListQuery } from '@entities/eventJournal/api/hooks';
import { EventJournalQueries } from '@entities/eventJournal/model/queries';
import { TEstimate } from '@entities/eventJournal/model/types';
import { useGetFilterNames } from '@features/JournalEventsFilters/lib';
import { useHandleForm } from '@shared/hooks';
import { CustomModal, Form, Popup, UiButton, UiText } from '@shared/ui';
import { useSendJournalEventModalForm, useDeleteJournalEvent } from '@widgets/EventJournal/api';
import {
    useDeletePopup,
    useGetEventItemData,
    useSyncEventJournalModalForm,
} from '@widgets/EventJournal/lib/hooks';

import cls from './EventJournalModal.module.css';
import {
    AttachmentsField,
    DescriptionField,
    EventDateField,
    SelectEstimatesField,
} from './ModalFormFields';
import { useEventJournalModal } from '../../lib/hooks';
import { IEventJournalModalSchema } from '../../model/types';

export const EventJournalModal = ({ estimates }: { estimates: TEstimate[] }) => {
    const { t } = useTranslation();
    const { currentEventIdQueryState, isEditMode, isCreateMode, isOpen, handleClose, modalMode } =
        useEventJournalModal();
    const { openDeletePopup, closeDeletePopup, showDeletePopup } = useDeletePopup();

    const { data: journalEventsList } = useGetJournalEventsListQuery();

    const eventData = useGetEventItemData({ currentEventIdQueryState, journalEventsList });

    const { handleSubmit: handleSendFormData, isPending } = useSendJournalEventModalForm({
        modalMode,
    });
    const { handleSubmit, control, reset, errors } = useHandleForm<IEventJournalModalSchema>({
        onSubmit: async (formData) => {
            await handleSendFormData(formData);
            handleClose();
            reset();
        },
        defaultValues: {
            selectEstimates: null,
            description: '',
            attachments: [],
            eventDate: '',
        },
        resetEnable: false,
    });
    useSyncEventJournalModalForm({ modalMode, isOpen, eventData, reset });

    const defaultValuesInEstimates = useGetFilterNames({
        filterData: estimates ?? [],
        queryKey: EventJournalQueries.estimate,
    });

    const formattedEstimatesData = estimates?.map((item) => ({
        isChecked: defaultValuesInEstimates?.includes(String(item.name)) ?? false,
        label: item.name,
        value: item.id,
        imgUrl: '',
    }));

    const { handleDelete, isPending: isDeletePending } = useDeleteJournalEvent({
        onSuccessCb: () => {
            closeDeletePopup();
            handleClose();
            reset();
        },
        onSettledCb: () => {
            closeDeletePopup();
        },
    });

    const hasList = !!journalEventsList && journalEventsList?.length >= 0;

    return (
        <>
            {isCreateMode && hasList && (
                <CustomModal
                    isOpen={isOpen}
                    onHideModal={() => {
                        handleClose();
                        reset();
                    }}
                    closeButtonClass={cls.CloseBtn}
                    contentClasses={cls.CustomModal}
                    isLoading={isPending}
                >
                    <CustomModal.Header>
                        <UiText tag="h4" variant="subHeading" weight="bold">
                            {t('JournalEvents.EventJournalModal.Create.Header')}
                        </UiText>
                    </CustomModal.Header>
                    <CustomModal.Body classes={cls.CustomModalBody}>
                        <Form onSubmit={handleSubmit} id="add-employer-form">
                            <DescriptionField
                                control={control}
                                t={t}
                                error={errors.description?.message}
                            />
                            <div className={cls.FieldsWrapper}>
                                <SelectEstimatesField
                                    control={control}
                                    estimates={formattedEstimatesData}
                                />
                                <EventDateField
                                    control={control}
                                    t={t}
                                    error={errors.eventDate?.message}
                                />
                            </div>
                            <AttachmentsField control={control} />
                        </Form>
                    </CustomModal.Body>
                    <CustomModal.Footer classes={cls.Footer}>
                        <UiButton
                            btnColor="secondary"
                            btnText={t('button.cancel.text')}
                            onClick={() => {
                                handleClose();
                                reset();
                            }}
                        />
                        <UiButton
                            type="submit"
                            form="add-employer-form"
                            btnColor="primary"
                            btnText={t('button.create.text')}
                        />
                    </CustomModal.Footer>
                </CustomModal>
            )}
            {isEditMode && hasList && (
                <CustomModal
                    isOpen={isOpen}
                    onHideModal={() => {
                        handleClose();
                        reset();
                    }}
                    closeButtonClass={cls.CloseBtn}
                    contentClasses={cls.CustomModal}
                    isLoading={isPending}
                >
                    <CustomModal.Header>
                        <UiText tag="h4" variant="subHeading" weight="bold">
                            {eventData?.eventItemData?.isYour
                                ? t('JournalEvents.EventJournalModal.Edit.Header')
                                : t('JournalEvents.EventJournalModal.View.Header')}
                        </UiText>
                    </CustomModal.Header>
                    <CustomModal.Body classes={cls.CustomModalBody}>
                        <Form onSubmit={handleSubmit} id="add-employer-form">
                            <DescriptionField
                                control={control}
                                t={t}
                                error={errors.description?.message}
                                isDisabled={!eventData?.eventItemData?.isYour}
                            />
                            <div className={cls.FieldsWrapper}>
                                <SelectEstimatesField
                                    estimates={formattedEstimatesData}
                                    control={control}
                                    isDisabled={!eventData?.eventItemData?.isYour}
                                />
                                <EventDateField
                                    control={control}
                                    t={t}
                                    isDisabled={!eventData?.eventItemData?.isYour}
                                />
                            </div>
                            <AttachmentsField
                                control={control}
                                isDisabled={!eventData?.eventItemData?.isYour}
                            />
                        </Form>
                    </CustomModal.Body>
                    <CustomModal.Footer classes={cn(cls.Footer, cls.EditFooter)}>
                        {eventData?.eventItemData?.isYour && (
                            <>
                                <UiButton
                                    variant="outlined"
                                    btnColor="danger"
                                    btnText={t('button.delete.text')}
                                    onClick={() => {
                                        openDeletePopup();
                                    }}
                                />
                                <UiButton
                                    type="submit"
                                    form="add-employer-form"
                                    btnColor="primary"
                                    btnText={t('button.save.text')}
                                />
                            </>
                        )}
                        {!eventData?.eventItemData?.isYour && (
                            <UiButton
                                btnColor="secondary"
                                btnText={t('button.close.text')}
                                onClick={() => {
                                    handleClose();
                                    reset();
                                }}
                                className={cls.marginLeftAuto}
                            />
                        )}
                    </CustomModal.Footer>
                </CustomModal>
            )}
            <Popup
                title={t('JournalEvents.DeletePopup')}
                type="delete"
                isOpen={showDeletePopup}
                onHidePopup={closeDeletePopup}
                btnActionCb={handleDelete}
                popupContentClassName={cls.PopupContent}
                headerClassName={cls.PopupHeader}
                footerClassName={cls.PopupFooter}
                isPending={isDeletePending}
            />
        </>
    );
};
